import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import {
  PageHeader, ConfirmDialog, EmptyState, useToast, SearchableSelect, InlineSpinner,
} from '../components/UI';
import { usePermissions } from '../context/PermissionsContext';
import { fmtDate } from '../utils/format';

const FORM_EMPTY = { course_code: '', intake_code: '', module_code: '', survey_url: '' };

export default function ESurveyLinksPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [courses, setCourses] = useState([]);
  const [intakes, setIntakes] = useState([]);
  const [modules, setModules] = useState([]);
  const [intakeModules, setIntakeModules] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(FORM_EMPTY);
  const [staged, setStaged] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadSaved = () => {
    api.get('/survey-links').then(r => setSaved(r.data.data || [])).catch(() => toast('Failed to load eSurvey links', 'error'));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/survey-links/options').then(r => {
        setCourses(r.data.data?.courses || []);
        setIntakes(r.data.data?.intakes || []);
        setModules(r.data.data?.modules || []);
        setIntakeModules(r.data.data?.intakeModules || []);
      }),
      api.get('/survey-links').then(r => setSaved(r.data.data || [])),
    ]).catch(() => toast('Failed to load eSurvey links', 'error')).finally(() => setLoading(false));
  }, []);

  // Same cascade as the Fee Rates form: intake choices narrow to the
  // selected course, module choices narrow to whatever's actually assigned
  // to the selected intake.
  const intakesForCourse = form.course_code ? intakes.filter(i => i.course_code === form.course_code) : [];
  const modulesForIntake = form.intake_code
    ? modules.filter(m => intakeModules.some(im => im.intake_code === form.intake_code && im.module_code === m.module_code))
    : [];

  const courseName = code => courses.find(c => c.course_code === code)?.course_name || code;
  const moduleName = code => modules.find(m => m.module_code === code)?.module_name || code;

  const handleAddToList = () => {
    if (!form.course_code || !form.intake_code || !form.module_code || !form.survey_url.trim()) {
      setError('Course, intake, module and a survey URL are all required.');
      return;
    }
    if (staged.some(s => s.intake_code === form.intake_code && s.module_code === form.module_code)) {
      setError('That intake and module combination is already in the staged list below.');
      return;
    }
    setError('');
    setStaged(list => [...list, { ...form, survey_url: form.survey_url.trim() }]);
    // Keep course/intake selected — adding several modules under the same
    // intake is the common case — clear only module + URL.
    setForm(f => ({ ...f, module_code: '', survey_url: '' }));
  };

  const removeStaged = idx => setStaged(list => list.filter((_, i) => i !== idx));

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await api.post('/survey-links/bulk', { links: staged });
      toast(`${staged.length} eSurvey link(s) saved`, 'success');
      setStaged([]);
      setForm(FORM_EMPTY);
      loadSaved();
    } catch (err) {
      toast(err.response?.data?.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/survey-links/${deleteTarget.id}`);
      toast('eSurvey link deleted', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Delete failed', 'error');
    } finally {
      setDeleteTarget(null);
      loadSaved();
    }
  };

  return (
    <>
      <PageHeader title="eSurvey Links" subtitle="Attach an external end-of-course survey to a course, intake and module" />

      {can('survey_links', 'create') && (
        <div className="sms-card mb-3" style={{ padding: 16 }}>
          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Course *</label>
              <SearchableSelect value={form.course_code}
                onChange={e => setForm(f => ({ ...f, course_code: e.target.value, intake_code: '', module_code: '' }))}
                placeholder="-- Select course --"
                options={courses.map(c => ({ value: c.course_code, label: c.course_name }))} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Intake *</label>
              <SearchableSelect value={form.intake_code}
                onChange={e => setForm(f => ({ ...f, intake_code: e.target.value, module_code: '' }))}
                placeholder={form.course_code ? '-- Select intake --' : '-- Select a course first --'}
                disabled={!form.course_code}
                options={intakesForCourse.map(i => ({ value: i.intake_code, label: i.intake_code, right: `${fmtDate(i.soc_date)} – ${fmtDate(i.eoc_date)}` }))} />
            </div>
            <div style={{ flex: '1 1 200px', minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Module *</label>
              <SearchableSelect value={form.module_code}
                onChange={e => setForm(f => ({ ...f, module_code: e.target.value }))}
                placeholder={form.intake_code ? '-- Select module --' : '-- Select an intake first --'}
                disabled={!form.intake_code}
                options={modulesForIntake.map(m => ({ value: m.module_code, label: m.module_name, right: m.module_code }))} />
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-end mt-2" style={{ gap: 12 }}>
            <div style={{ flex: '1 1 320px', minWidth: 220 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--sms-txt2)', display: 'block', marginBottom: 5 }}>Survey URL *</label>
              <input className="sms-form-control" placeholder="https://forms.office.com/…"
                value={form.survey_url} onChange={e => setForm(f => ({ ...f, survey_url: e.target.value }))} />
            </div>
            <button type="button" className="sms-btn sms-btn-outline" onClick={handleAddToList}>
              <i className="bi bi-plus-lg" /> Add to list
            </button>
          </div>
          {error && <div className="alert alert-danger py-2 mt-2" style={{ fontSize: 13 }}>{error}</div>}

          {staged.length > 0 && (
            <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--sms-bdr)' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--sms-txt2)' }}>
                  Staged — not yet saved
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, background: 'var(--sms-btn-primary-bg)', color: '#fff', borderRadius: 999, padding: '1px 8px' }}>
                  {staged.length}
                </span>
              </div>
              {staged.map((s, idx) => (
                <div key={`${s.intake_code}-${s.module_code}`} className="d-flex align-items-center justify-content-between"
                  style={{ background: 'var(--sms-sur2)', borderRadius: 10, padding: '8px 12px', marginBottom: 6, gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>{courseName(s.course_code)} · {s.intake_code} · {moduleName(s.module_code)}</div>
                    <div style={{ fontSize: 12, color: 'var(--sms-acc)', fontFamily: 'ui-monospace, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.survey_url}</div>
                  </div>
                  <button type="button" className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 8px' }} onClick={() => removeStaged(idx)}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              ))}
              <div className="d-flex justify-content-end mt-2" style={{ gap: 10 }}>
                <button type="button" className="sms-btn sms-btn-outline" onClick={() => setStaged([])}>Discard staged</button>
                <button type="button" className="sms-btn sms-btn-primary" disabled={saving} onClick={handleSaveAll}>
                  {saving ? <InlineSpinner /> : <><i className="bi bi-check-lg" /> Save all ({staged.length})</>}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="sms-card" style={{ padding: 0, overflow: 'hidden' }}>
        {!loading && saved.length === 0 ? (
          <EmptyState icon="bi-link-45deg" message="No eSurvey links saved yet" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="sms-table">
              <thead><tr>
                <th>Course</th>
                <th>Intake</th>
                <th>Module</th>
                <th>Survey URL</th>
                {can('survey_links', 'delete') && <th style={{ width: 60 }}>Actions</th>}
              </tr></thead>
              <tbody>
                {saved.map(r => (
                  <tr key={r.id}>
                    <td>{r.course_name || r.course_code || '—'}</td>
                    <td>{r.intake_code}</td>
                    <td>{r.module_name || r.module_code || '—'}</td>
                    <td style={{ color: 'var(--sms-acc)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <a href={r.survey_url} target="_blank" rel="noreferrer">{r.survey_url}</a>
                    </td>
                    {can('survey_links', 'delete') && (
                      <td>
                        <button className="sms-btn sms-btn-outline-danger" style={{ padding: '4px 6px' }} title="Delete" onClick={() => setDeleteTarget(r)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmDialog show={!!deleteTarget} danger
        message={deleteTarget ? `Delete the eSurvey link for ${deleteTarget.intake_code} · ${deleteTarget.module_name || deleteTarget.module_code}?` : ''}
        onConfirm={handleDelete} onCancel={() => setDeleteTarget(null)} />
    </>
  );
}
